import { ReactElement } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { BookOpen, CircleHelp, Scale } from "lucide-react";
import { Banner } from "~/components/Banner";
import { CityGrid } from "~/components/CityGrid";
import ServiceSelector from "~/components/ServiceSelector";
import SectionStrip from "~/components/SectionStrip";
import SolutionSelector from "~/components/SolutionSelector";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { CitiesHubContent as content } from "~/content/miasta/content.pl";
import { ROUTES } from "~/content/Routes";
import { CITIES, CITY_COORDINATES, findCityByName } from "~/lib/cities";
import { NextPageWithLayout } from "~/pages/_app";
import { BG, TEXT } from "~/components/theme/colors";
import type { CityMarker } from "~/components/PolandMap";

const PolandMap = dynamic(() => import("~/components/PolandMap"), {
  ssr: false,
  loading: () => (
    <div className="flex min-h-[560px] items-center justify-center rounded-xl bg-gray-50 text-sm text-gray-400">
      Ładowanie mapy…
    </div>
  ),
});

const CitiesHubPage: NextPageWithLayout = () => {
  const router = useRouter();

  const allCityMarkers: CityMarker[] = CITIES.map((c) => {
    const coords = CITY_COORDINATES[c.slug];
    return { name: c.name, lat: coords.lat, lng: coords.lng };
  });

  const handleCityClick = (city: CityMarker) => {
    const found = findCityByName(city.name);
    if (found) void router.push(`/${found.slug}/`);
  };

  return (
    <>
      <Banner title={content.bannerTitle} />

      <section className="bg-white py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(18rem,0.9fr)] lg:items-stretch">
            <PolandMap
              cities={allCityMarkers}
              center={[52.1, 19.4]}
              zoom={6}
              height="580px"
              onCityClick={handleCityClick}
            />

            <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                {content.mapAside.eyebrow}
              </p>
              <h2 className="mt-3 text-2xl font-bold leading-tight text-slate-900">
                {content.mapAside.title}
              </h2>

              <div className="mt-4 space-y-4 text-sm leading-6 text-slate-600">
                {content.mapAside.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>

              <ul className="mt-5 space-y-3 border-t border-slate-200 pt-5">
                {content.mapAside.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-sky-600" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-sm font-medium leading-6 text-slate-700">
                {content.mapAside.closing}
              </p>
            </aside>
          </div>
        </div>
      </section>

      <CityGrid
        cities={CITIES}
        title={content.cityGrid.title}
        subtitle={content.cityGrid.subtitle}
        showAllByDefault
      />

      <SectionStrip
        title={content.serviceStripTitle}
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
      />
      <MarginLayout>
        <ServiceSelector />
      </MarginLayout>

      <SectionStrip
        title={content.knowledgeStripTitle}
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
      />
      <MarginLayout>
        <SolutionSelector
          items={[
            { icon: BookOpen, label: "Baza Wiedzy", href: ROUTES.BLOG },
            { icon: Scale, label: "Legislacja", href: ROUTES.LEGISLATION },
            { icon: CircleHelp, label: "FAQ", href: ROUTES.FAQ },
          ]}
          gridClassName="grid-cols-3 max-w-xl mx-auto gap-4 mb-10"
        />
      </MarginLayout>
    </>
  );
};

CitiesHubPage.getLayout = (page: ReactElement) => (
  <MainLayout>
    <NextSeo
      title={content.seo.title}
      description={content.seo.description}
    />
    {page}
  </MainLayout>
);

export default CitiesHubPage;
